import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { IdTokenClaims } from '@azure/msal-common';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription, filter, take } from 'rxjs';
import { enableRouteTracker } from './core/helper/route-tracker.helper';
import { AuthenticationService } from './core/services/auth.service';
import { IdleTimerService } from './core/services/idle-timer.service';
import { LoginUser } from './core/models/user.model';
import { App, YoungPeopleLiceseId } from './app.helper';
import { TerminologyService } from './services/terminology/terminology.service';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  private subs = new Subscription();
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private modalService: BsModalService,
    private idleTimerService: IdleTimerService,
    private terminologyService: TerminologyService
  ) {
    this.modalService.config.backdrop = 'static';
  }

  ngOnInit() {
    const sub = enableRouteTracker(this.router, this.activatedRoute);
    this.subs.add(sub);

    // Navigating away must dismiss any open modals — otherwise in-modal actions that
    // route (e.g. "View Activity" inside the session-history dropdown of the engagement
    // detail modal) load the new page BEHIND the still-open dialog. hide() with no id
    // closes the whole modal stack.
    const navModalSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe(() => {
        if (this.modalService.getModalsCount() > 0) {
          this.modalService.hide();
        }
      });
    this.subs.add(navModalSub);

    this.authService.initiAuth();

    const userSub = this.authService.currentUserObs
      .pipe(filter((user): user is LoginUser => !!user), take(1))
      .subscribe((user) => {
        this.idleTimerService.start();

        // Push the resolved terminology values into App.contactLabel* so legacy
        // {{ contactLabelSingular }} / {{ contactLabelPlural }} consumers pick
        // up the licence's customised wording without per-template edits. The
        // YoungPeople licence keeps its hardcoded "Young Person" / "Young
        // People" until that licence configures its own terminology overrides
        // in the admin screen — preserves current behaviour, no data migration
        // needed today.
        const applyContactLabels = () => {
          const resolved = this.terminologyService.getAllResolved();
          if (resolved?.['contact_singular']) {
            App.contactLabelSingular = resolved['contact_singular'];
          }
          if (resolved?.['contact_plural']) {
            App.contactLabelPlural = resolved['contact_plural'];
          }
          if (user.licenseId === YoungPeopleLiceseId) {
            App.contactLabelSingular = 'Young Person';
            App.contactLabelPlural = 'Young People';
          }
        };

        // Warm the terminology cache so TermPipe / consumers can render the
        // licence's wording from the first paint instead of falling back to
        // the canonical defaults. Errors are non-fatal — the pipe falls back
        // gracefully to defaults.
        this.terminologyService.load().subscribe({
          next: applyContactLabels,
          error: (e) => {
            // Deliberately loud: a swallowed failure here renders raw term keys
            // app-wide (e.g. "programme_singular") with nothing in the console
            // to say why — that cost us a debugging session in July 2026.
            console.error('[terminology] GetTerminology failed — labels will fall back to raw term keys', e);
            applyContactLabels();
          },
        });
        // Re-apply when the catalogue refreshes (admin saved overrides).
        const catSub = this.terminologyService.catalogue().subscribe(() => applyContactLabels());
        this.subs.add(catSub);
      });
    this.subs.add(userSub);
  }

  // unsubscribe to events when component is destroyed
  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.idleTimerService.stop();
    this.authService.destroyAuth();
  }
}
